import {
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  DecisionsSavedEvent,
  makeCountryRadioFieldModel,
  ProjectCompany,
} from "@/dbot/common/commonDomain";
import { makeAutoObservable } from "mobx";
import { DecisionType } from "@/common/decisions";
import {
  BooleanFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType";
import { emailValidator, PhoneNumber } from "@/common/common";
import { newUuid } from "@/utils/uuid";
import { TabButtonProps } from "@/components-v2/Tabs";
import { isNorwegianOrgNo } from "@/utils/orgNo";
import { DropdownMenuItemProps, DropdownMenuProps } from "@/components-v2/Dropdown";
import { TMessage } from "@/i18n/tt";
import { IconName } from "@/components-v2/iconMapping";
import { identity, isEqual, sortBy, uniq } from "lodash";
import { confirmAction, thunk } from "@/events/eventsDomain";
import { noDialog } from "@/dbot/summary/summaryDomain";
import { boardRoles, getFullName, sortedBoard, validateName } from "@/utils/Roles";
import { CompanyRole } from "@/types/CompanyInfo";
import { Language } from "@/i18n/languages";
import { PhoneInputModel } from "@/components-v2/PhoneInput";
import { isNotBlank, unblank } from "@/utils/strings";
import { ActorsSelectorModel, ActorType, loadActors } from "@/types/SelectActors";
import { resolveUrnReference, toCompanyRoleReference } from "@/utils/UrnReference";
import { IdentificationForPerson } from "@/dbot/summary/SummaryController";
import { dNumberValidator, norwegianSocialSecurityNumberValidator } from "@/utils/other";
import { DocumentType } from "@/common/documents";
import compact from "lodash/compact";
import { norway } from "@/utils/countries";

export type PersonalDeputyFor = { id: string; name: string };

export enum ElectionOfBoardType {
  reElectBoardMembers = "reElectBoardMembers",
  removeAllBoardMembers = "removeAllBoardMembers",
  updateBoardMember = "updateBoardMember",
  addBoardMember = "addBoardMember",
  removeBoardMember = "removeBoardMember",
}

export type ElectionOfBoardMode =
  | ReElectBoardMembers
  | RemoveAllBoardMembers
  | UpdateBoardMember
  | AddBoardMember
  | RemoveBoardMember;

export type ReElectBoardMembers = { type: ElectionOfBoardType.reElectBoardMembers };
export type RemoveAllBoardMembers = { type: ElectionOfBoardType.removeAllBoardMembers };
export type UpdateBoardMember = {
  type: ElectionOfBoardType.updateBoardMember;
  id: string;
  boardChangeId?: string;
  companyRoleId?: number;
  role: RoleType;
  name: string;
  nationality?: string;
  socialSecurityNumber?: string;
  personReference?: string;
  dnr?: string;
  email?: string;
  personalDeputyFor?: PersonalDeputyFor[];
  excludeOrgNos: string[];
};
export type AddBoardMember = {
  type: ElectionOfBoardType.addBoardMember;
  id: string;
  companyRoleId?: number;
  role: RoleType;
  name: string;
  nationality?: string;
  socialSecurityNumber?: string;
  personReference?: string;
  dnr?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  personalDeputyFor?: PersonalDeputyFor[];
  excludeOrgNos: string[];
};
export type RemoveBoardMember = {
  type: ElectionOfBoardType.removeBoardMember;
  id: string;
  boardChangeId?: string;
  companyRoleId?: number;
  name: string;
  socialSecurityNumber?: string;
  personReference?: string;
  excludeOrgNos: string[];
};

function isPersonChange(
  change: ElectionOfBoardMode,
): change is AddBoardMember | UpdateBoardMember | RemoveBoardMember {
  return (
    change.type === ElectionOfBoardType.addBoardMember ||
    change.type === ElectionOfBoardType.updateBoardMember ||
    change.type === ElectionOfBoardType.removeBoardMember
  );
}

function findPersonChange(changes: ElectionOfBoardMode[], id: string) {
  return changes.filter(isPersonChange).find((change) => change.id === id);
}

function boardChangesOf(
  decisions: DecisionsSavedEvent["saved"],
  decisionId: string,
): ElectionOfBoardMode[] {
  const data = decisions.find((d) => d.decisionId === decisionId)?.decisionData;
  return data?.type === DecisionType.electionOfCompanyBoard ? data.boardChanges : [];
}

export class ElectionOfBoardChangeModel {
  id: string = newUuid();
  data: ElectionOfBoardMode;

  constructor(data: ElectionOfBoardMode) {
    this.data = data;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  onSubmitEdit(data: ElectionOfBoardMode) {
    this.data = data;
  }

  get personChangeId(): string | undefined {
    return isPersonChange(this.data) ? this.data.id : undefined;
  }

  syncExcludeOrgNos(saved: ElectionOfBoardMode[]) {
    const change = this.data;
    if (change.type === ElectionOfBoardType.addBoardMember) {
      const excludeOrgNos = findPersonChange(saved, change.id)?.excludeOrgNos;
      if (
        excludeOrgNos !== undefined &&
        !isEqual(sortBy(excludeOrgNos), sortBy(change.excludeOrgNos))
      ) {
        this.data = { ...change, excludeOrgNos };
      }
    }
  }

  get isReElectionOfBoardMembers() {
    return this.data.type === ElectionOfBoardType.reElectBoardMembers;
  }

  get isRemoveAllBoardMembers() {
    return this.data.type === ElectionOfBoardType.removeAllBoardMembers;
  }

  includeBoardChangeForCompany(orgNo: string) {
    switch (this.data.type) {
      case ElectionOfBoardType.updateBoardMember:
      case ElectionOfBoardType.removeBoardMember:
      case ElectionOfBoardType.addBoardMember:
        return !this.data.excludeOrgNos.includes(orgNo);
      default:
        return true;
    }
  }

  toModel(): ElectionOfBoardMode {
    switch (this.data.type) {
      case ElectionOfBoardType.addBoardMember:
      case ElectionOfBoardType.updateBoardMember:
        return {
          ...this.data,
          personalDeputyFor: this.data.personalDeputyFor?.map((personal) => personal),
          excludeOrgNos: this.data.excludeOrgNos?.map((orgNo) => orgNo),
        };
      case ElectionOfBoardType.removeBoardMember:
        return {
          ...this.data,
          excludeOrgNos: this.data.excludeOrgNos?.map((orgNo) => orgNo),
        };
      default:
        return this.data;
    }
  }
}

export type ElectionOfCompanyBoard = {
  type: DecisionType.electionOfCompanyBoard;
  boardChanges: ElectionOfBoardMode[];
  boardMeetingProtocolShowCompanyBoard: boolean;
  noticeGeneralMeetingShowCompanyBoard: boolean;
  genderCompositionRequirement?: boolean;
};

export type NewCompanyRole = {
  id: string;
  boardChangeId?: string;
  companyRoleId?: number;
  role: RoleType;
  name: string;
  nationality?: string;
  socialSecurityNumber?: string;
  personReference?: string;
  roleIdsOfPerson?: number[];
  dnr?: string;
  email?: string;
  personalDeputyFor?: PersonalDeputyFor[];
};

export type CompanyBoardPreviewTab = "currentBoard" | "newBoard";

export type ElectionOfBoardDialogState =
  | { type: "none" }
  | { type: "addBoardMember"; model: AddToElectionOfBoardModel }
  | { type: "addBoardMemberFromPortfolio"; model: ActorsSelectorModel }
  | { type: "updateBoardMember"; model: UpdateElectionOfBoardModel }
  | { type: "removeBoardMember"; model: RemoveElectionOfBoardModel };

export class ElectionOfCompanyBoardForm {
  readonly type = DecisionType.electionOfCompanyBoard;

  boardChanges: ElectionOfBoardChangeModel[];
  boardMeetingProtocolShowCompanyBoard: BooleanFieldModel;
  noticeGeneralMeetingShowCompanyBoard: BooleanFieldModel;
  genderCompositionRequirement: BooleanFieldModel;

  boardChangeDialogState: ElectionOfBoardDialogState = noDialog();
  selectedCompanyBoardPreviewTab: CompanyBoardPreviewTab;

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<ElectionOfCompanyBoard>,
    data?: ElectionOfCompanyBoard,
  ) {
    this.boardChanges =
      data?.boardChanges.map((boardChange) => new ElectionOfBoardChangeModel(boardChange)) ?? [];

    this.boardMeetingProtocolShowCompanyBoard = new BooleanFieldModel({
      value: data?.boardMeetingProtocolShowCompanyBoard ?? false,
    });
    this.noticeGeneralMeetingShowCompanyBoard = new BooleanFieldModel({
      value: data?.noticeGeneralMeetingShowCompanyBoard ?? false,
    });
    this.genderCompositionRequirement = new BooleanFieldModel({
      value: data?.genderCompositionRequirement,
      required: true,
    });

    this.selectedCompanyBoardPreviewTab = this.isNewBoardTabDisabled ? "currentBoard" : "newBoard";
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isMainCompany() {
    return this.context.isMainCompany;
  }

  get isBatchProject() {
    return this.context.isBatchProject;
  }

  get containsBoardMeetingMinutes() {
    return this.context.currentDocuments.some((a) => a.type === DocumentType.board_meeting);
  }

  get containsNoticeGeneralMeeting() {
    return this.context.currentDocuments.some(
      (a) => a.type === DocumentType.notice_general_meeting,
    );
  }

  get showCurrentBoard(): boolean {
    return this.selectedCompanyBoardPreviewTab === "currentBoard";
  }

  closeDialog() {
    this.boardChangeDialogState = noDialog();
  }

  get isNorwegianCompany() {
    return isNorwegianOrgNo(this.context.orgNo);
  }

  get companyName(): string {
    return this.context.initialState.company.name;
  }

  get isNewBoardTabDisabled() {
    return !this.containsBoardChanges || this.isNewCompanyBoardEmpty;
  }

  setActiveTab(tab: CompanyBoardPreviewTab) {
    this.selectedCompanyBoardPreviewTab = tab;
  }

  get companyBoard() {
    return sortedBoard(this.context.initialState.roles);
  }

  get boardChangesSummaryList() {
    return this.isBatchProject
      ? this.boardChanges
      : this.updateRolesInBoardChangesPerCompany(this.context.orgNo);
  }

  get newCompanyBoard() {
    return this.boardAfterChanges(
      this.companyBoard,
      this.updateRolesInBoardChangesPerCompany(this.context.orgNo),
    );
  }

  get isNewBoardMissingChairperson() {
    return this.newCompanyBoard.every((a) => a.role !== RoleType.CHAIRPERSON);
  }

  get isNewDeputyChairperson() {
    return this.newCompanyBoard.some(
      (a) => a.role === RoleType.DEPUTY_CHAIRPERSON && a.boardChangeId !== undefined,
    );
  }

  boardAfterChanges(companyBoard: CompanyRole[], boardChanges: ElectionOfBoardChangeModel[]) {
    let newBoard = toNewCompanyRoles(companyBoard);
    boardChanges.forEach((boardChange) => {
      newBoard = changeCompanyBoard(boardChange.data, newBoard);
    });
    return sortedNewCompanyRoles(newBoard);
  }

  get containsBoardChanges() {
    return this.boardChanges.length > 0;
  }

  get isNewCompanyBoardEmpty() {
    return this.newCompanyBoard.length === 0;
  }

  get isReElectionOfBoardMembers() {
    return (
      this.containsBoardChanges &&
      this.boardChanges.some((model) => model.isReElectionOfBoardMembers)
    );
  }

  get containsRemoveAllBoardMembers() {
    return this.boardChanges.some((model) => model.isRemoveAllBoardMembers);
  }

  addBoardChange(boardChange: ElectionOfBoardMode) {
    this.boardChanges.push(new ElectionOfBoardChangeModel(boardChange));
    this.setActiveTab(this.isNewBoardTabDisabled ? "currentBoard" : "newBoard");
  }

  get companies() {
    return this.context.companies;
  }

  get rolesPerCompany() {
    return Object.entries(this.context.contextData).reduce(
      (acc, [orgNo, context]) => {
        acc[orgNo] = sortedBoard(context.initialState.roles);
        return acc;
      },
      {} as { [orgNo: string]: CompanyRole[] },
    );
  }

  get newRolesPerCompany() {
    return Object.entries(this.rolesPerCompany).reduce(
      (acc, [orgNo, roles]) => {
        acc[orgNo] = this.boardAfterChanges(roles, this.updateRolesInBoardChangesPerCompany(orgNo));
        return acc;
      },
      {} as { [orgNo: string]: NewCompanyRole[] },
    );
  }

  updateRolesInBoardChangesPerCompany(orgNo: string): ElectionOfBoardChangeModel[] {
    return this.boardChanges.filter((boardChange) =>
      boardChange.includeBoardChangeForCompany(orgNo),
    );
  }

  editBoardChange(locale: Language, model: ElectionOfBoardChangeModel) {
    switch (model.data.type) {
      case ElectionOfBoardType.updateBoardMember:
        this.boardChangeDialogState = {
          type: "updateBoardMember",
          model: new UpdateElectionOfBoardModel(
            locale,
            this.isBatchProject,
            this.companies,
            this.newRolesPerCompany,
            model.onSubmitEdit,
            this.closeDialog,
            model.data,
          ),
        };
        break;
      case ElectionOfBoardType.addBoardMember:
        this.boardChangeDialogState = {
          type: "addBoardMember",
          model: new AddToElectionOfBoardModel(
            locale,
            this.isBatchProject,
            this.companies,
            this.newRolesPerCompany,
            this.newCompanyBoard,
            model.onSubmitEdit,
            this.closeDialog,
            false,
            model.data,
          ),
        };
        break;
    }
  }

  onSelectBoardChange(locale: Language, type: ElectionOfBoardType) {
    switch (type) {
      case ElectionOfBoardType.removeAllBoardMembers:
      case ElectionOfBoardType.reElectBoardMembers:
        this.addBoardChange({ type });
        break;
      case ElectionOfBoardType.removeBoardMember:
        this.boardChangeDialogState = {
          type: "removeBoardMember",
          model: new RemoveElectionOfBoardModel(
            locale,
            this.isBatchProject,
            this.companies,
            this.newRolesPerCompany,
            this.addBoardChange,
            this.closeDialog,
          ),
        };
        break;
      case ElectionOfBoardType.updateBoardMember:
        this.boardChangeDialogState = {
          type: "updateBoardMember",
          model: new UpdateElectionOfBoardModel(
            locale,
            this.isBatchProject,
            this.companies,
            this.newRolesPerCompany,
            this.addBoardChange,
            this.closeDialog,
          ),
        };
        break;
      case ElectionOfBoardType.addBoardMember:
        this.boardChangeDialogState = {
          type: "addBoardMember",
          model: new AddToElectionOfBoardModel(
            locale,
            this.isBatchProject,
            this.companies,
            this.newRolesPerCompany,
            this.newCompanyBoard,
            this.addBoardChange,
            this.closeDialog,
            true,
          ),
        };
        break;
    }
  }

  get excludeActorsInBatchProject() {
    const allRoles = Object.values(this.newRolesPerCompany);
    return uniq(
      allRoles
        .flat()
        .filter((member) =>
          allRoles.every((roles) => roles.some((role) => isSameMember(role, member))),
        )
        .flatMap(memberReferences),
    );
  }

  get excludeActors(): string[] {
    return this.isBatchProject
      ? this.excludeActorsInBatchProject
      : this.newCompanyBoard.flatMap(memberReferences);
  }

  openAddNewShareholderFromPortfolioDialog(locale: Language) {
    this.boardChangeDialogState = {
      type: "addBoardMemberFromPortfolio",
      model: openAddBoardMemberFromPortfolioDialog(
        this.context.orgNo,
        this.dispatch,
        this.excludeActors,
        (data) => {
          this.boardChangeDialogState = {
            type: "addBoardMember",
            model: new AddToElectionOfBoardModel(
              locale,
              this.isBatchProject,
              this.companies,
              this.newRolesPerCompany,
              this.newCompanyBoard,
              this.addBoardChange,
              this.closeDialog,
              true,
              data,
            ),
          };
        },
        this.closeDialog,
      ),
    };
  }

  showDeleteBoardChangeDialog(id: string) {
    this.dispatch(
      confirmAction({
        text: { key: "decisions.electionOfCompanyBoard.boardChange.delete.confirm" },
        onConfirm: [thunk(() => this.deleteBoardchange(id))],
      }),
    );
  }

  deleteBoardchange(id: string) {
    this.boardChanges = this.boardChanges.filter((d) => d.id !== id);
    this.setActiveTab(this.isNewBoardTabDisabled ? "currentBoard" : "newBoard");
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get duplicatePersonBoardChangeIds(): string[] {
    const reported = Object.values(this.context.contextData).flatMap(
      (company) =>
        company.inputStates.find((s) => s.decisionId === this.context.decisionId)
          ?.duplicateEntryIds ?? [],
    );
    const addedNowhere = this.isBatchProject
      ? this.boardChanges.flatMap(({ data }) =>
          data.type === ElectionOfBoardType.addBoardMember &&
          this.companies.every((company) => data.excludeOrgNos.includes(company.orgNo))
            ? [data.id]
            : [],
        )
      : [];
    return uniq([...reported, ...addedNowhere]);
  }

  hasDuplicatePerson(model: ElectionOfBoardChangeModel): boolean {
    return (
      model.personChangeId !== undefined &&
      this.duplicatePersonBoardChangeIds.includes(model.personChangeId)
    );
  }

  handleEvent(event: DbotEvent) {
    if (event.type === "dbot/decisions-saved") {
      const saved = boardChangesOf(event.saved, this.context.decisionId);
      this.boardChanges.forEach((model) => model.syncExcludeOrgNos(saved));
    }
  }

  get isValid() {
    return (
      this.containsBoardChanges &&
      !this.isNewCompanyBoardEmpty &&
      this.duplicatePersonBoardChangeIds.length === 0 &&
      this.boardMeetingProtocolShowCompanyBoard.isValid &&
      this.noticeGeneralMeetingShowCompanyBoard.isValid &&
      this.genderCompositionRequirement.isValid
    );
  }

  validate(): boolean {
    this.boardMeetingProtocolShowCompanyBoard.validate();
    this.noticeGeneralMeetingShowCompanyBoard.validate();
    this.genderCompositionRequirement.validate();
    return this.isValid;
  }
}

export class AddToElectionOfBoardModel {
  data?: AddBoardMember;
  roleType: RadioFieldModel<RoleType | null>;
  name: StringFieldModel;
  nationality: RadioFieldModel<{ code: string; name: string }>;
  socialSecurityNumber: StringFieldModel;
  dnr: StringFieldModel;
  email: StringFieldModel;
  phoneNumber: PhoneInputModel;
  personalDeputyFor: OptionFieldModel<PersonalDeputyFor>;

  constructor(
    readonly locale: Language,
    readonly isBatchProject: boolean,
    readonly companies: ProjectCompany[],
    readonly newRolesPerCompany: { [orgNo: string]: NewCompanyRole[] },
    readonly newRoles: NewCompanyRole[],
    readonly onSubmit: (data: ElectionOfBoardMode) => void,
    readonly onClose: () => void,
    readonly isNewBoardChange: boolean,
    data?: AddBoardMember,
  ) {
    this.data = data;
    this.roleType = new RadioFieldModel({
      value: data?.role ?? null,
      options: boardRoles,
      idFn: identity,
      labelFn: (item) =>
        item !== null
          ? getRoleTypeLabel(item)
          : { key: "decisions.electionOfCompanyBoard.boardChange.dialog.role.placeholder" },
      required: true,
    });
    this.name = new StringFieldModel({
      value: data?.name,
      required: true,
      validator: validateName,
    });
    this.nationality = makeCountryRadioFieldModel(locale, data?.nationality);
    this.socialSecurityNumber = new StringFieldModel({
      value: data?.socialSecurityNumber,
      required: isBatchProject,
      validator: (value) => {
        if (this.isOnAllBoards) {
          return {
            key: "decisions.electionOfCompanyBoard.boardChange.dialog.socialSecurityNumber.existing-ssn.all-boards",
          };
        } else if (
          this.nationality.value.code === "NO" &&
          !isShownNumber(value, this.data?.socialSecurityNumber)
        ) {
          return norwegianSocialSecurityNumberValidator(value);
        }
      },
    });
    this.dnr = new StringFieldModel({
      value: data?.dnr,
      validator: dNumberValidator,
      required: isBatchProject && this.nationality.value.code !== norway,
    });
    this.email = new StringFieldModel({
      value: data?.email,
      validator: emailValidator,
      required: isBatchProject,
    });
    this.phoneNumber = new PhoneInputModel({ value: data?.phoneNumber, locale });

    this.personalDeputyFor = new OptionFieldModel({
      value: data?.personalDeputyFor ?? [],
      options: toPersonalDeputyFor(this.newRoles),
      idFn: (item) => item.id,
      labelFn: (item) => ({ translated: item.name }),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setNationality(value: { code: string; name: string }) {
    this.nationality.setValue(value);
    this.dnr.setRequired(this.isBatchProject && value.code !== norway);
  }

  get companiesContainingSSN() {
    return this.companies.filter((company) => this.orgNosContainingSSN.includes(company.orgNo));
  }

  get orgNosContainingSSN() {
    const companyRoleId = this.data?.companyRoleId;
    return isShownNumber(
      this.socialSecurityNumber.validatedValue,
      this.data?.socialSecurityNumber,
    ) && companyRoleId !== undefined
      ? Object.entries(this.newRolesPerCompany).flatMap(([orgNo, roles]) =>
          roles.some((role) => (role.roleIdsOfPerson ?? []).includes(companyRoleId)) ? [orgNo] : [],
        )
      : [];
  }

  get isOnAllBoards() {
    return (
      this.isBatchProject &&
      this.isNewBoardChange &&
      this.orgNosContainingSSN.length === Object.keys(this.newRolesPerCompany).length
    );
  }

  get isNewBoardChangeOrSocialSecurityNumber() {
    return (
      this.isNewBoardChange ||
      this.data?.socialSecurityNumber !== this.socialSecurityNumber.validatedValue
    );
  }

  get showBatchProjectInformation() {
    return (
      this.isBatchProject &&
      this.isNewBoardChangeOrSocialSecurityNumber &&
      this.companiesContainingSSN.length > 0
    );
  }

  get showPersonalDeputyFor() {
    return this.roleType.value === RoleType.DEPUTY_MEMBER && !this.isBatchProject;
  }

  submit() {
    if (this.validate()) {
      this.onSubmit(this.toAddBoardMember());
      this.onClose();
    }
  }

  toAddBoardMember(): AddBoardMember {
    return {
      type: ElectionOfBoardType.addBoardMember,
      id: this.data?.id ?? newUuid(),
      companyRoleId: this.data?.companyRoleId,
      role: this.roleType.value ?? RoleType.MEMBER,
      name: this.name.validatedValue,
      nationality: this.nationality.value.code,
      socialSecurityNumber: unblank(this.socialSecurityNumber.validatedValue),
      personReference: this.data?.personReference,
      dnr: this.dnr.isEmpty ? undefined : this.dnr.validatedValue,
      email: this.email.isEmpty ? undefined : this.email.validatedValue,
      phoneNumber: this.phoneNumber.toValidPhoneNumber(),
      personalDeputyFor: this.showPersonalDeputyFor ? this.personalDeputyFor.selected : undefined,
      excludeOrgNos:
        this.isBatchProject && this.isNewBoardChangeOrSocialSecurityNumber
          ? this.orgNosContainingSSN
          : (this.data?.excludeOrgNos ?? []),
    };
  }

  get isValid() {
    return (
      this.roleType.isValid &&
      this.name.isValid &&
      this.nationality.isValid &&
      this.socialSecurityNumber.isValid &&
      this.dnr.isValid &&
      this.email.isValid &&
      this.phoneNumber.isValid &&
      this.personalDeputyFor.isValid
    );
  }

  validate(): boolean {
    this.roleType.validate();
    this.name.validate();
    this.nationality.validate();
    this.socialSecurityNumber.validate();
    this.dnr.validate();
    this.email.validate();
    this.phoneNumber.validate();
    this.personalDeputyFor.validate();
    return this.isValid;
  }
}

export class UpdateElectionOfBoardModel {
  data?: UpdateBoardMember;
  selectedMember: RadioFieldModel<NewCompanyRole | null>;
  roleType: RadioFieldModel<RoleType | null>;
  nationality: RadioFieldModel<{ code: string; name: string }>;
  socialSecurityNumber: StringFieldModel;
  dnr: StringFieldModel;
  email: StringFieldModel;
  personalDeputyFor: OptionFieldModel<PersonalDeputyFor>;

  constructor(
    readonly locale: Language,
    readonly isBatchProject: boolean,
    readonly companies: ProjectCompany[],
    readonly newRolesPerCompany: { [orgNo: string]: NewCompanyRole[] },
    readonly onSubmit: (data: ElectionOfBoardMode) => void,
    readonly onClose: () => void,
    data?: UpdateBoardMember,
  ) {
    this.data = data;
    this.selectedMember = new RadioFieldModel({
      value: null,
      options: this.memberOptions,
      idFn: (item) => item?.id ?? "null",
      labelFn: (item) =>
        item !== null
          ? { translated: item.name }
          : {
              key: "decisions.electionOfCompanyBoard.boardChange.dialog.selectBoardMember.placeholder",
            },
      required: data === undefined,
      descriptionFn: (item) =>
        isBatchProject
          ? getCompanyDescription(this.companies, this.findCompaniesForRole(item))
          : undefined,
      onChange: (item) => {
        this.updateSocialSecurityNumber(item?.socialSecurityNumber);
        this.updateEmail(item?.email);
      },
    });
    this.roleType = new RadioFieldModel({
      value: this.data?.role ?? null,
      options: boardRoles,
      idFn: identity,
      labelFn: (item) =>
        item !== null
          ? getRoleTypeLabel(item)
          : { key: "decisions.electionOfCompanyBoard.boardChange.dialog.role.placeholder" },
      required: true,
    });

    this.nationality = makeCountryRadioFieldModel(locale, this.data?.nationality);
    this.socialSecurityNumber = new StringFieldModel({
      value: this.data?.socialSecurityNumber,
      required: isBatchProject,
      validator: (value) =>
        this.nationality.value.code === "NO" &&
        !isShownNumber(value, (this.data ?? this.selectedMember.value)?.socialSecurityNumber)
          ? norwegianSocialSecurityNumberValidator(value)
          : undefined,
    });
    this.dnr = new StringFieldModel({
      value: this.data?.dnr,
      validator: dNumberValidator,
      required: isBatchProject && this.nationality.value.code !== norway,
    });
    this.email = new StringFieldModel({
      value: data?.email,
      validator: emailValidator,
      required: isBatchProject,
    });
    this.personalDeputyFor = new OptionFieldModel({
      value: this.data?.personalDeputyFor ?? [],
      options: () => this.personalDeputyForOptions,
      idFn: (item) => item.id,
      labelFn: (item) => ({ translated: item.name }),
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  updateSocialSecurityNumber(value?: string) {
    if (value) {
      this.socialSecurityNumber.setAndCommitInputValue(value);
    } else {
      this.socialSecurityNumber.setInputValue("");
    }
  }

  updateEmail(value?: string) {
    if (value) {
      this.email.setAndCommitInputValue(value);
    } else {
      this.email.setInputValue("");
    }
  }

  setNationality(value: { code: string; name: string }) {
    this.nationality.setValue(value);
    this.dnr.setRequired(this.isBatchProject && value.code !== norway);
  }

  get memberOptions() {
    return getBoardMemberOptions(this.newRolesPerCompany);
  }

  findOrgNosForRole(newRole: NewCompanyRole | null) {
    return Object.entries(this.newRolesPerCompany).flatMap(([orgNo, roles]) =>
      roles.some((role) => this.isSelectedMember(role, newRole)) ? [orgNo] : [],
    );
  }

  isSelectedMember(role: NewCompanyRole, newRole: NewCompanyRole | null) {
    return (
      role.id === (this.data?.id ?? newRole?.id) ||
      matchesMember(
        role,
        this.data ? this.data.companyRoleId : newRole?.companyRoleId,
        this.data ? this.data.boardChangeId : newRole?.boardChangeId,
      )
    );
  }

  get excludedOrgNos() {
    const changeAffectingOrgNos = this.findOrgNosForRole(this.selectedMember.value);
    return this.companies
      .map((a) => a.orgNo)
      .filter((orgNo) => !changeAffectingOrgNos.includes(orgNo));
  }

  findCompaniesForRole(newRole: NewCompanyRole | null) {
    return this.companies.filter((company) =>
      this.findOrgNosForRole(newRole).includes(company.orgNo),
    );
  }

  findNewRolesExcludingCurrentMember(roles: NewCompanyRole[]) {
    return roles.filter((role) => !this.isSelectedMember(role, this.selectedMember.value));
  }

  get numberOfCompaniesForSelectedRole() {
    return this.findCompaniesForRole(this.selectedMember.value).length;
  }

  get remainingNumberOfCompaniesForSelectedRole() {
    return this.companies.length - this.numberOfCompaniesForSelectedRole;
  }

  get showBatchProjectInformation() {
    return (
      this.isBatchProject &&
      this.selectedMember.value !== null &&
      this.numberOfCompaniesForSelectedRole !== this.companies.length
    );
  }

  get personalDeputyForOptions(): PersonalDeputyFor[] {
    return toPersonalDeputyFor(this.findNewRolesExcludingCurrentMember(this.memberOptions));
  }

  get isEdit() {
    return this.data !== undefined;
  }

  get showPersonalDeputyFor() {
    return this.roleType.value === RoleType.DEPUTY_MEMBER && !this.isBatchProject;
  }

  submit() {
    if (this.validate()) {
      this.onSubmit(this.toUpdateBoardMember());
      this.onClose();
    }
  }

  toUpdateBoardMember(): UpdateBoardMember {
    return {
      type: ElectionOfBoardType.updateBoardMember,
      id: this.data?.id ?? newUuid(),
      boardChangeId: this.selectedMember.value?.boardChangeId,
      companyRoleId: this?.data
        ? this?.data?.companyRoleId
        : this.selectedMember.value?.companyRoleId,
      name: this?.data ? this.data.name : (this.selectedMember.value?.name ?? ""),
      role: this.roleType.value ?? RoleType.MEMBER,
      nationality: this.nationality.value.code,
      socialSecurityNumber: unblank(this.socialSecurityNumber.validatedValue),
      personReference: this.data
        ? this.data.personReference
        : memberReference(this.selectedMember.value),
      dnr: this.dnr.isEmpty ? undefined : this.dnr.validatedValue,
      email: this.email.isEmpty ? undefined : this.email.validatedValue,
      personalDeputyFor: this.showPersonalDeputyFor ? this.personalDeputyFor.selected : undefined,
      excludeOrgNos:
        this.isBatchProject &&
        this.data?.socialSecurityNumber !== this.socialSecurityNumber.validatedValue
          ? this.excludedOrgNos
          : (this.data?.excludeOrgNos ?? []),
    };
  }

  get isValid() {
    return (
      this.selectedMember.isValid &&
      this.roleType.isValid &&
      this.nationality.isValid &&
      this.socialSecurityNumber.isValid &&
      this.dnr.isValid &&
      this.email.isValid &&
      this.personalDeputyFor.isValid
    );
  }

  validate(): boolean {
    this.selectedMember.validate();
    this.roleType.validate();
    this.nationality.validate();
    this.socialSecurityNumber.validate();
    this.dnr.validate();
    this.email.validate();
    this.personalDeputyFor.validate();
    return this.isValid;
  }
}

export class RemoveElectionOfBoardModel {
  selectedMember: RadioFieldModel<NewCompanyRole | null>;

  constructor(
    readonly locale: Language,
    readonly isBatchProject: boolean,
    readonly companies: ProjectCompany[],
    readonly newRolesPerCompany: { [orgNo: string]: NewCompanyRole[] },
    readonly onSubmit: (data: ElectionOfBoardMode) => void,
    readonly onClose: () => void,
  ) {
    this.selectedMember = new RadioFieldModel({
      value: null,
      options: this.memberOptions,
      idFn: (item) => item?.id ?? "null",
      descriptionFn: (item) =>
        isBatchProject
          ? getCompanyDescription(this.companies, this.findCompaniesForRole(item))
          : undefined,
      labelFn: (item) =>
        item !== null
          ? { translated: item.name }
          : {
              key: "decisions.electionOfCompanyBoard.boardChange.dialog.selectBoardMember.placeholder",
            },
      required: true,
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get memberOptions() {
    return getBoardMemberOptions(this.newRolesPerCompany);
  }

  findOrgNosForRole(newRole: NewCompanyRole | null) {
    return Object.entries(this.newRolesPerCompany).flatMap(([orgNo, roles]) =>
      newRole !== null &&
      roles.some((role) => newRole.id === role.id || isSameMember(newRole, role))
        ? [orgNo]
        : [],
    );
  }

  get excludedOrgNos() {
    const changeAffectingOrgNos = this.findOrgNosForRole(this.selectedMember.value);
    return this.companies
      .map((a) => a.orgNo)
      .filter((orgNo) => !changeAffectingOrgNos.includes(orgNo));
  }

  findCompaniesForRole(newRole: NewCompanyRole | null) {
    return this.companies.filter((company) =>
      this.findOrgNosForRole(newRole).includes(company.orgNo),
    );
  }

  get numberOfCompaniesForSelectedRole() {
    return this.findCompaniesForRole(this.selectedMember.value).length;
  }

  get remainingNumberOfCompaniesForSelectedRole() {
    return this.companies.length - this.numberOfCompaniesForSelectedRole;
  }

  get showBatchProjectInformation() {
    return (
      this.isBatchProject &&
      this.selectedMember.value !== null &&
      this.numberOfCompaniesForSelectedRole !== this.companies.length
    );
  }

  submit() {
    if (this.validate()) {
      this.onSubmit(this.toRemoveBoardMember());
      this.onClose();
    }
  }

  toRemoveBoardMember(): RemoveBoardMember {
    const selectedBoardMember = this.selectedMember.value;
    return {
      type: ElectionOfBoardType.removeBoardMember,
      id: newUuid(),
      boardChangeId: selectedBoardMember?.boardChangeId,
      companyRoleId: selectedBoardMember?.companyRoleId,
      name: selectedBoardMember?.name ?? "",
      socialSecurityNumber: unblank(selectedBoardMember?.socialSecurityNumber ?? ""),
      personReference: memberReference(selectedBoardMember),
      excludeOrgNos: this.excludedOrgNos,
    };
  }

  get isValid() {
    return this.selectedMember.isValid;
  }

  validate(): boolean {
    this.selectedMember.validate();
    return this.isValid;
  }
}

export function toNewCompanyRoles(companyRoles: CompanyRole[]): NewCompanyRole[] {
  return companyRoles.map((member) => ({
    id: newUuid(),
    companyRoleId: member.companyroleId,
    socialSecurityNumber: isNotBlank(member.socialSecurityNumber)
      ? member.socialSecurityNumber
      : undefined,
    roleIdsOfPerson: [member.companyroleId, ...(member.samePersonRoleIds ?? [])],
    name: getFullName(member),
    email: member.email,
    role: member.role,
  }));
}

export function sortedNewCompanyRoles(roles: NewCompanyRole[]) {
  return sortBy(
    roles,
    (r) => boardRoles.indexOf(r.role),
    (r) => r.name,
  );
}

export function changeCompanyBoard(
  boardChange: ElectionOfBoardMode,
  currentBoard: NewCompanyRole[],
): NewCompanyRole[] {
  switch (boardChange.type) {
    case ElectionOfBoardType.reElectBoardMembers:
      return currentBoard;
    case ElectionOfBoardType.removeAllBoardMembers:
      return [];
    case ElectionOfBoardType.updateBoardMember:
      return currentBoard.map((member) =>
        isSelectedRole(member, boardChange.boardChangeId, boardChange.companyRoleId)
          ? {
              ...member,
              boardChangeId: boardChange.id,
              role: boardChange.role,
              nationality: boardChange.nationality,
              socialSecurityNumber: boardChange.socialSecurityNumber,
              personReference: boardChange.personReference,
              dnr: boardChange.dnr,
              email: boardChange.email,
              personalDeputyFor: boardChange.personalDeputyFor,
            }
          : member,
      );
    case ElectionOfBoardType.addBoardMember:
      return [
        ...currentBoard,
        {
          id: boardChange.id,
          boardChangeId: boardChange.id,
          companyRoleId: boardChange.companyRoleId,
          name: boardChange.name,
          role: boardChange.role,
          nationality: boardChange.nationality,
          socialSecurityNumber: boardChange.socialSecurityNumber,
          personReference: boardChange.personReference,
          roleIdsOfPerson:
            boardChange.companyRoleId === undefined ? [] : [boardChange.companyRoleId],
          dnr: boardChange.dnr,
          email: boardChange.email,
          personalDeputyFor: boardChange.personalDeputyFor,
        },
      ];
    case ElectionOfBoardType.removeBoardMember:
      return currentBoard.filter(
        (member) => !isSelectedRole(member, boardChange.boardChangeId, boardChange.companyRoleId),
      );
  }
}

function isSelectedRole(
  role: NewCompanyRole,
  boardChangeId: string | undefined,
  companyRoleId: number | undefined,
): boolean {
  return companyRoleId !== undefined && companyRoleId !== null
    ? isRoleOfPerson(role, companyRoleId)
    : boardChangeId === role.boardChangeId;
}

function matchesMember(
  role: NewCompanyRole,
  companyRoleId: number | undefined,
  boardChangeId: string | undefined,
): boolean {
  return (
    (companyRoleId !== undefined && isRoleOfPerson(role, companyRoleId)) ||
    (boardChangeId !== undefined && role.boardChangeId === boardChangeId)
  );
}

function isRoleOfPerson(role: NewCompanyRole, companyRoleId: number): boolean {
  return (
    role.companyRoleId === companyRoleId || (role.roleIdsOfPerson ?? []).includes(companyRoleId)
  );
}

function isSameMember(a: NewCompanyRole, b: NewCompanyRole): boolean {
  return matchesMember(b, a.companyRoleId, a.boardChangeId);
}

function memberReference(member: NewCompanyRole | null | undefined): string | undefined {
  return member?.companyRoleId !== undefined
    ? toCompanyRoleReference(member.companyRoleId)
    : member?.personReference;
}

function memberReferences(member: NewCompanyRole): string[] {
  return uniq(
    compact([
      ...(member.roleIdsOfPerson ?? []).map(toCompanyRoleReference),
      memberReference(member),
      member.personReference,
    ]),
  );
}

function isShownNumber(value: string | undefined, shown: string | undefined): boolean {
  return (
    shown !== undefined && shown !== "" && value?.replace(/\s/g, "") === shown.replace(/\s/g, "")
  );
}

export function isPersonalDeputy(member: NewCompanyRole) {
  return (
    member.role === RoleType.DEPUTY_MEMBER &&
    member.personalDeputyFor !== undefined &&
    member.personalDeputyFor.length > 0
  );
}

export function prepareShowBoardTabs(form: ElectionOfCompanyBoardForm): TabButtonProps[] {
  return [
    {
      id: "currentBoard",
      label: {
        key: "decisions.electionOfCompanyBoard.tabs.currentBoard",
        params: { n: { type: "number", value: form.companyBoard.length } },
      },
      isActive: form.selectedCompanyBoardPreviewTab === "currentBoard",
      onSelect: () => form.setActiveTab("currentBoard"),
    },
    {
      id: "newBoard",
      label: form.containsBoardChanges
        ? {
            key: "decisions.electionOfCompanyBoard.tabs.newBoard",
            params: { n: { type: "number", value: form.newCompanyBoard.length } },
          }
        : { key: "decisions.electionOfCompanyBoard.tabs.newBoard.default" },
      isActive: form.selectedCompanyBoardPreviewTab === "newBoard",
      onSelect: () => form.setActiveTab("newBoard"),
      disabled: form.isNewBoardTabDisabled,
    },
  ];
}

export function prepareBoardChangeItems(
  locale: Language,
  form: ElectionOfCompanyBoardForm,
): DropdownMenuProps["items"] {
  const updateAndRemoveActions =
    !form.containsRemoveAllBoardMembers && !form.isNewCompanyBoardEmpty
      ? [ElectionOfBoardType.updateBoardMember, ElectionOfBoardType.removeBoardMember]
      : [];
  const individualActions = [ElectionOfBoardType.addBoardMember, ...updateAndRemoveActions];
  const bulkActions =
    !form.containsBoardChanges && (!form.isNewCompanyBoardEmpty || form.isBatchProject)
      ? [ElectionOfBoardType.reElectBoardMembers, ElectionOfBoardType.removeAllBoardMembers]
      : [];
  return [
    ...individualActions.map<DropdownMenuItemProps>((type) => ({
      type: "item",
      label: boardChangeItemLabel[type],
      icon: boardChangeItemIcon[type],
      onSelect: () => form.onSelectBoardChange(locale, type),
    })),
    {
      type: "label",
      label: { key: "decisions.electionOfCompanyBoard.boardChange.button.item.bulk-label" },
    },
    {
      type: "item",
      label: { key: "decisions.electionOfCompanyBoard.boardChange.button.item.add.portfolio" },
      icon: "suitcaseSimple",
      onSelect: () => form.openAddNewShareholderFromPortfolioDialog(locale),
    },
    ...bulkActions.map<DropdownMenuItemProps>((type) => ({
      type: "item",
      label: boardChangeItemLabel[type],
      icon: boardChangeItemIcon[type],
      onSelect: () => form.onSelectBoardChange(locale, type),
    })),
  ];
}

export const boardChangeItemLabel: { [key in ElectionOfBoardType]: TMessage } = {
  [ElectionOfBoardType.updateBoardMember]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.update",
  },
  [ElectionOfBoardType.removeBoardMember]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.remove",
  },
  [ElectionOfBoardType.addBoardMember]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.add",
  },
  [ElectionOfBoardType.reElectBoardMembers]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.reElect",
  },
  [ElectionOfBoardType.removeAllBoardMembers]: {
    key: "decisions.electionOfCompanyBoard.boardChange.button.item.removeAll",
  },
};

export const boardChangeItemIcon: { [key in ElectionOfBoardType]: IconName } = {
  [ElectionOfBoardType.updateBoardMember]: "user-switch",
  [ElectionOfBoardType.removeBoardMember]: "minus",
  [ElectionOfBoardType.addBoardMember]: "plus",
  [ElectionOfBoardType.reElectBoardMembers]: "arrow-clockwise",
  [ElectionOfBoardType.removeAllBoardMembers]: "stack-minus",
};

export function boardChangeSummaryDescription(
  data: ElectionOfBoardMode,
  isBatchProject: boolean,
  totalNumberOfCompanies: number,
): TMessage {
  switch (data.type) {
    case ElectionOfBoardType.addBoardMember:
      return {
        key: "decisions.electionOfCompanyBoard.boardChange.description.add",
        params: {
          name: data.name,
          role: { type: "uncapitilize", value: getRoleTypeLabel(data.role) },
        },
      };
    case ElectionOfBoardType.removeBoardMember:
      return {
        key: "decisions.electionOfCompanyBoard.boardChange.description.remove",
        params: { name: data.name },
      };
    case ElectionOfBoardType.updateBoardMember: {
      const excludedCompaniesLength = data.excludeOrgNos.length;
      return {
        key: "decisions.electionOfCompanyBoard.boardChange.description.update",
        params: {
          name: data.name,
          role: { type: "uncapitilize", value: getRoleTypeLabel(data.role) },
          companiesCount:
            isBatchProject && excludedCompaniesLength !== 0
              ? {
                  key: "decisions.electionOfCompanyBoard.boardChange.description.update.companiesCount",
                  params: {
                    x: {
                      type: "number",
                      value: totalNumberOfCompanies - excludedCompaniesLength,
                    },
                    total: {
                      type: "number",
                      value: totalNumberOfCompanies,
                    },
                  },
                }
              : "",
        },
      };
    }
    case ElectionOfBoardType.reElectBoardMembers:
      return { key: "decisions.electionOfCompanyBoard.boardChange.description.reElect" };
    case ElectionOfBoardType.removeAllBoardMembers:
      return { key: "decisions.electionOfCompanyBoard.boardChange.description.removeAll" };
  }
}

export function toPersonalDeputyFor(newRoles: NewCompanyRole[]) {
  return newRoles
    .filter((a) => a.role !== RoleType.DEPUTY_MEMBER)
    .map((newRole) => ({
      id: newRole?.companyRoleId?.toString() ?? newRole.id,
      name: newRole.name,
    }));
}

function openAddBoardMemberFromPortfolioDialog(
  orgNo: string,
  dispatch: DbotEventDispatcher,
  excludeActors: string[],
  onSubmit: (boardChange: AddBoardMember) => void,
  onClose: () => void,
): ActorsSelectorModel {
  return new ActorsSelectorModel({
    title: { key: "decisions.electionOfCompanyBoard.boardChange.button.item.add.portfolio.title" },
    organisationNumber: orgNo,
    tabs: [ActorType.BOARD_MEMBER, ActorType.CEO, ActorType.SHAREHOLDER],
    excludeActors: excludeActors,
    excludeCompanies: true,
    isMultiSelect: false,
    defaultCompanyFilterValue: null,
    onLoad: (type, onSuccess, onFailure) => loadActors(type, onSuccess, onFailure, dispatch, orgNo),
    onSubmit: (selectedActors) => {
      const selectedActor = selectedActors[0];
      const reference = resolveUrnReference(selectedActor.reference);
      onSubmit({
        type: ElectionOfBoardType.addBoardMember,
        id: newUuid(),
        companyRoleId:
          reference?.type === "companyroleId" && selectedActor.organisationNumber === orgNo
            ? reference.id
            : undefined,
        role: RoleType.MEMBER,
        name: selectedActor.name,
        nationality: selectedActor.nationality,
        email: selectedActor.email,
        phoneNumber: selectedActor.phoneNumber,
        socialSecurityNumber: selectedActor.socialSecurityNumber,
        personReference: selectedActor.reference,
        excludeOrgNos: [],
      });
    },
    onClose,
  });
}

export function getMissingIdentificationDocumentations(
  boardChanges: ElectionOfBoardMode[],
): IdentificationForPerson[] {
  let membersForIdentification: IdentificationForPerson[] = [];
  boardChanges.forEach((boardChange) => {
    membersForIdentification = getRoleForIdentification(boardChange, membersForIdentification);
  });
  return membersForIdentification;
}

function getRoleForIdentification(
  boardChange: ElectionOfBoardMode,
  membersForIdentification: IdentificationForPerson[],
): IdentificationForPerson[] {
  switch (boardChange.type) {
    case ElectionOfBoardType.reElectBoardMembers:
      return [];
    case ElectionOfBoardType.removeAllBoardMembers:
      return [];
    case ElectionOfBoardType.updateBoardMember: {
      const existing = membersForIdentification.find(
        (member) =>
          (member.companyRoleId !== undefined &&
            member.companyRoleId === boardChange.companyRoleId) ||
          member.id === boardChange.boardChangeId,
      );
      return existing
        ? [
            ...removeRoleForIdentification(
              membersForIdentification,
              boardChange.boardChangeId,
              boardChange.companyRoleId,
            ),
            toRoleForIdentification(boardChange),
          ]
        : [...membersForIdentification, toRoleForIdentification(boardChange)];
    }
    case ElectionOfBoardType.addBoardMember:
      return [...membersForIdentification, toRoleForIdentification(boardChange)];
    case ElectionOfBoardType.removeBoardMember:
      return removeRoleForIdentification(
        membersForIdentification,
        boardChange.boardChangeId,
        boardChange.companyRoleId,
      );
  }
}

function removeRoleForIdentification(
  membersForIdentification: IdentificationForPerson[],
  boardChangeId?: string,
  companyRoleId?: number,
): IdentificationForPerson[] {
  return membersForIdentification.filter(
    (member) =>
      (companyRoleId === undefined && member.id !== boardChangeId) ||
      (companyRoleId !== undefined && member.companyRoleId !== companyRoleId),
  );
}

function toRoleForIdentification(role: {
  id: string;
  companyRoleId?: number;
  name: string;
  nationality?: string;
  socialSecurityNumber?: string;
  dnr?: string;
  email?: string;
}): IdentificationForPerson {
  return {
    id: role.id,
    companyRoleId: role.companyRoleId,
    name: role.name,
    nationality: role.nationality,
    identityNumber: role.socialSecurityNumber ?? "",
    dnr: role.dnr,
    email: role.email,
  };
}

function getCompanyDescription(
  allCompanies: ProjectCompany[],
  companies: ProjectCompany[],
): TMessage {
  return companies.length === 1
    ? { translated: companies[0].name }
    : allCompanies.length === companies.length
      ? { key: "all-companies" }
      : {
          key: "decisions.electionOfCompanyBoard.boardChange.dialog.selectBoardMember.found-in-companies",
          params: { count: { type: "number", value: companies.length } },
        };
}

function getBoardMemberOptions(newRolesPerCompany: { [orgNo: string]: NewCompanyRole[] }) {
  const allRoles = Object.values(newRolesPerCompany).flatMap((roles) => roles);
  return allRoles.filter(
    (item, index, self) => index === self.findIndex((t) => t === item || isSameMember(t, item)),
  );
}
